import { useMemo, useState } from 'react'
import { Message } from '@arco-design/web-react'
import { IconCheck, IconCopy } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import { AnthropicLogo, GeminiLogo, OpenAILogo } from '@/components/Landing/ProviderLogos'
import { getRelayOrigin, joinRelayPath } from '@/lib/apiBaseUrl'
import { useStatusStore } from '@/stores/statusStore'

type ProtocolId = 'openai' | 'anthropic' | 'gemini'
type EndpointSuffix = 'v1' | 'root'

const PROTOCOL_LOGOS = {
  openai: OpenAILogo,
  anthropic: AnthropicLogo,
  gemini: GeminiLogo,
} as const

function CopyButton({
  label,
  copied,
  onCopy,
}: {
  label: string
  copied: boolean
  onCopy: () => void
}) {
  return (
    <button
      type="button"
      className={`landing-quick-start-copy-btn${copied ? ' is-copied' : ''}`}
      onClick={onCopy}
      aria-label={label}
    >
      {copied ? <IconCheck /> : <IconCopy />}
    </button>
  )
}

export default function QuickStartCard() {
  const { t } = useTranslation()
  const serverAddress = useStatusStore((s) => s.status?.server_address)
  const [protocol, setProtocol] = useState<ProtocolId>('openai')
  const [endpointSuffix, setEndpointSuffix] = useState<EndpointSuffix>('v1')
  const [copied, setCopied] = useState(false)

  const relayOrigin = useMemo(() => getRelayOrigin(serverAddress), [serverAddress])

  const sdkBaseUrl = useMemo(() => {
    if (protocol === 'openai') {
      return endpointSuffix === 'v1' ? joinRelayPath(relayOrigin, '/v1') : relayOrigin
    }
    return relayOrigin
  }, [protocol, relayOrigin, endpointSuffix])

  const handleProtocolChange = (id: ProtocolId) => {
    setProtocol(id)
    setEndpointSuffix(id === 'openai' ? 'v1' : 'root')
    setCopied(false)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sdkBaseUrl)
      setCopied(true)
      Message.success(t('landing.quickStartCard.copied'))
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      Message.error(t('复制失败'))
    }
  }

  const steps = [
    t('landing.quickStartCard.stepApiKey'),
    t('landing.quickStartCard.stepProtocol'),
    t('landing.quickStartCard.stepCall'),
  ]

  const protocols: { id: ProtocolId; labelKey: string }[] = [
    { id: 'openai', labelKey: 'landing.quickStartCard.openai' },
    { id: 'anthropic', labelKey: 'landing.quickStartCard.anthropic' },
    { id: 'gemini', labelKey: 'landing.quickStartCard.gemini' },
  ]

  const showEndpointTabs = protocol === 'openai'

  return (
    <div className="landing-hero-panel landing-quick-start-card landing-reveal-up landing-reveal-delay-5">
      <div className="landing-quick-start-header">
        <div className="landing-quick-start-header-text">
          <h3 className="landing-quick-start-title">{t('landing.quickStartCard.title')}</h3>
          <p className="landing-quick-start-subtitle">{t('landing.quickStartCard.subtitle')}</p>
        </div>
        <span className="landing-quick-start-status">{t('landing.quickStartCard.statusReady')}</span>
      </div>

      <ol className="landing-quick-start-steps-compact">
        {steps.map((step, index) => (
          <li key={step}>
            <span className="landing-quick-start-step-index">{index + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="landing-quick-start-section">
        <div className="landing-quick-start-section-label">{t('landing.quickStartCard.protocol')}</div>
        <div className="landing-quick-start-protocol-tabs" role="tablist">
          {protocols.map((item) => {
            const Logo = PROTOCOL_LOGOS[item.id]
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={protocol === item.id}
                className={`landing-quick-start-protocol-tab${protocol === item.id ? ' is-active' : ''}`}
                onClick={() => handleProtocolChange(item.id)}
              >
                <span className={`landing-quick-start-protocol-icon landing-quick-start-protocol-icon-${item.id}`}>
                  <Logo size={14} />
                </span>
                {t(item.labelKey)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="landing-quick-start-code-block">
        <div className="landing-quick-start-terminal-header">
          <div className="landing-quick-start-terminal-dots" aria-hidden>
            <span className="landing-quick-start-terminal-dot landing-quick-start-terminal-dot-red" />
            <span className="landing-quick-start-terminal-dot landing-quick-start-terminal-dot-yellow" />
            <span className="landing-quick-start-terminal-dot landing-quick-start-terminal-dot-green" />
          </div>
          <span className="landing-quick-start-terminal-title">
            {t('landing.quickStartCard.terminalTitle')}
          </span>
          <span className="landing-quick-start-terminal-ready">
            {t('landing.quickStartCard.terminalReady')}
          </span>
        </div>

        <div className="landing-quick-start-code-row">
          <div className="landing-quick-start-code-label-row">
            <div className="landing-quick-start-code-label">{t('landing.quickStartCard.sdkBaseUrl')}</div>
            <span className="landing-quick-start-code-hint">{t('landing.quickStartCard.sdkBaseUrlHint')}</span>
          </div>

          {showEndpointTabs && (
            <div className="landing-quick-start-endpoint-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={endpointSuffix === 'v1'}
                className={`landing-quick-start-endpoint-tab${endpointSuffix === 'v1' ? ' is-active' : ''}`}
                onClick={() => {
                  setEndpointSuffix('v1')
                  setCopied(false)
                }}
              >
                {t('landing.quickStartCard.endpointV1')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={endpointSuffix === 'root'}
                className={`landing-quick-start-endpoint-tab${endpointSuffix === 'root' ? ' is-active' : ''}`}
                onClick={() => {
                  setEndpointSuffix('root')
                  setCopied(false)
                }}
              >
                {t('landing.quickStartCard.endpointRoot')}
              </button>
            </div>
          )}

          <div className="landing-quick-start-code-value">
            <code>{sdkBaseUrl}</code>
            <CopyButton
              label={t('landing.quickStartCard.copySdkBaseUrl')}
              copied={copied}
              onCopy={handleCopy}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
